import React from 'react'
import { useRouter } from 'next/navigation'

import { useSession, useTenant } from '@rewardops/auth'
import { useLocale } from '@rewardops/ui'
import { PromotionBannerProps } from '@rewardops/utils'

import { useErrorToast } from '~/components/error-toast'
import { useTranslation } from '~/app/i18n/client'
import { useFilterBySegmentationTags } from '../hooks/use-filter-by-segmentation-tags'
import MapleButton from '../maple/components/button'
import ResponsiveComponent from '../responsive-component'

type Props = {
  content: PromotionBannerProps
}

const ProductPromotionBanner: React.FC<Props> = (props: Props) => {
  const {
    isDark,
    backgroundColor,
    backgroundImage,
    backgroundImageMobile,
    title,
    description,
    subText,
    link,
  } = props?.content

  const { push } = useRouter()
  const tenant = useTenant()
  const { data: session } = useSession()
  const { triggerErrorToast } = useErrorToast()
  const { t } = useTranslation()
  const { locale } = useLocale()
  const isBannerSupportedForSegment = useFilterBySegmentationTags({
    bannerContent: props?.content,
  })

  const handlePromoClick = () => {
    if (
      link?.to?.includes('/travel') &&
      tenant?.favicon === 'mbna' &&
      !session?.user?.member?.id
    ) {
      triggerErrorToast({
        exclamation: t('travelLoginErrorToast.exclamation'),
        message: t('travelLoginErrorToast.message'),
      })
      return
    }

    if (link?.isRelativeUrl !== false) {
      return push(link?.to ?? '/')
    }

    const externalLink = link?.to?.startsWith('/')
      ? `${tenant?.publicUrl}/${locale}/${link?.to}`
      : link?.to

    window.open(externalLink, '_blank')
  }

  const promoInfo = (
    <>
      <div>
        <div
          className={`${
            tenant?.fonts?.secondary
              ? 'text-base font-semibold uppercase'
              : 'text-xs font-normal'
          } leading-[24px] ${isDark ? 'text-white' : 'text-black'}`}
          style={
            tenant?.fonts?.secondary
              ? { fontFamily: 'var(--font-secondary)' }
              : undefined
          }
        >
          {title}
        </div>
        <div
          className={`text-5xl font-normal leading-[56px] ${
            isDark ? 'text-white' : 'text-black'
          }`}
        >
          {description}
        </div>
      </div>
      {subText ? (
        <div
          className={`text-base font-normal leading-[24px] ${
            isDark ? 'text-white' : 'text-black'
          }`}
        >
          {subText}
        </div>
      ) : null}

      {link?.name && (
        <MapleButton
          id={`product-promo-btn-${title}`}
          className={`h-[60px] w-[212px] shrink-0 px-4 text-base font-semibold ${
            isDark ? 'bg-white text-black hover:bg-[#f5f5f7]' : ''
          }`}
          onClick={handlePromoClick}
        >
          {link?.name}
        </MapleButton>
      )}
    </>
  )

  const mobileComponent = (
    <div className="col-span-2 mx-auto mb-[60px] w-full max-w-full overflow-hidden">
      <div className="flex h-auto flex-col">
        <div
          className="flex-1 p-6 py-14"
          style={{ background: backgroundColor }}
        >
          <div className="flex h-full flex-col justify-center gap-6">
            {promoInfo}
          </div>
        </div>
        <div className="min-h-[300px]">
          <img
            className="h-[300px] w-full object-cover"
            src={
              backgroundImageMobile
                ? backgroundImageMobile?.file?.url
                : backgroundImage?.file?.url
            }
            alt={backgroundImage?.title ?? 'product promo image'}
          />
        </div>
      </div>
    </div>
  )

  const desktopComponent = (
    <div className="col-span-2 mx-auto mb-[100px] h-auto w-full max-w-[1140px] overflow-hidden">
      <div className="flex h-[432px] w-full">
        <div
          className="flex-1 rounded-l-3xl p-8 md:p-20"
          style={{ background: backgroundColor }}
        >
          <div className="flex h-full flex-col justify-center gap-6">
            {promoInfo}
          </div>
        </div>
        <div className="flex-1">
          <img
            className="h-full w-full rounded-r-3xl object-cover"
            src={backgroundImage?.file?.url}
            alt={backgroundImage?.title ?? 'product promo image'}
          />
        </div>
      </div>
    </div>
  )

  if (!Boolean(isBannerSupportedForSegment)) {
    return null
  }

  return (
    <ResponsiveComponent
      desktopComponent={desktopComponent}
      mobileComponent={mobileComponent}
    />
  )
}

export { ProductPromotionBanner }
